import React, { useCallback, useState } from 'react';
import dynamic from 'next/dynamic';
import { IconBellOutlineDuo18 } from 'nucleo-ui-outline-duo-18';

import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Popover, Tooltip } from '@lyearn/ui';
import { sidebarIconStrokeWidth } from '@/modules/corporate/modules/admin/components/Sidebar/nav-icon';
import {
  SIDEBAR_FOCUS_RING,
  TOP_BAR_ACTION_HOVER,
  TOP_BAR_ACTION_RING,
  TOP_BAR_ICON_SURFACE,
  TOP_BAR_ICON_TEXT,
} from '@/modules/corporate/modules/admin/components/Sidebar/theme';
import { MediaKindEnum, NotificationActionTypeEnum, PointsConfigTypeEnum } from '@/types/schema';

import useNotificationCount from '../NotificationButton/hooks/useNotificationCount';
import type { NotificationDetailsFragment } from '../NotificationTray/graphql/fragments/generated/NotificationDetails';

const NotificationTray = dynamic(() => import('../NotificationTray'), { ssr: false });
const BadgePreviewModal = dynamic(
  () => import('@/modules/gamification/components/BadgeGroupView/BadgePreviewModal'),
  { ssr: false },
);
const PointsPreviewModal = dynamic(
  () => import('@/modules/gamification/components/Points/PointsPreviewModal'),
  { ssr: false },
);

type BadgeNotificationType = {
  id?: string;
  message?: string;
  icon?: string | null;
};

const NotificationBell = () => {
  const { t } = useTranslation();
  const { onClick, unresolvedNotificationCount, unreadNotificationsCount, toggle, open } =
    useNotificationCount();

  const [currentBadge, setCurrentBadge] = useState<BadgeNotificationType | undefined>(undefined);
  const [pointsMessage, setPointsMessage] = useState<string | undefined>(undefined);

  const onClickBadgeType = useCallback((notif: NotificationDetailsFragment) => {
    if (notif.type === NotificationActionTypeEnum.BadgeUnlocked) {
      setCurrentBadge({
        id: notif.id,
        message: notif.message,
        icon: notif.icon ?? undefined,
      });
    }
  }, []);

  const onClickPointsAwarded = useCallback((notif: NotificationDetailsFragment) => {
    if (notif.type === NotificationActionTypeEnum.PointsAwarded) {
      setPointsMessage(notif.message);
    }
  }, []);

  const closeBadgeModal = useCallback(() => setCurrentBadge(undefined), []);
  const closePointsModal = useCallback(() => setPointsMessage(undefined), []);

  return (
    <>
      <Popover open={open} onOpenChange={onClick}>
        <Popover.Button>
          <Tooltip placement="bottom" title={`${t('Common:Notifications.Header')}`}>
            <button
              aria-label={`${t('Common:Notifications.Header')}`}
              className={cn(
                'relative flex h-32 w-32 flex-shrink-0 cursor-pointer items-center justify-center rounded-8 ring-1 ring-inset transition-colors duration-100',
                TOP_BAR_ICON_SURFACE,
                TOP_BAR_ICON_TEXT,
                TOP_BAR_ACTION_RING,
                TOP_BAR_ACTION_HOVER,
                SIDEBAR_FOCUS_RING,
              )}
              data-action="Open Notification Tray"
              type="button">
              <IconBellOutlineDuo18
                className="h-18 w-18"
                strokeWidth={sidebarIconStrokeWidth(18)}
              />
              {unresolvedNotificationCount ? (
                <span className="absolute top-6 h-6 w-6 rounded-full bg-icon-error end-6" />
              ) : null}
            </button>
          </Tooltip>
        </Popover.Button>
        <Popover.Content className="shadow-03" position="bottom-end" renderAs="popover">
          <NotificationTray
            toggleTray={toggle}
            unreadNotificationsCount={unreadNotificationsCount}
            onClickBadgeType={onClickBadgeType}
            onClickPointsAwarded={onClickPointsAwarded}
          />
        </Popover.Content>
      </Popover>
      {currentBadge ? (
        <BadgePreviewModal
          open
          badge={{
            id: currentBadge.id ?? '',
            name: 'Congratulations!',
            achievementRemark: currentBadge.message,
            pointsConfig: {
              points: 0,
              bonus: 0,
              type: PointsConfigTypeEnum.Custom,
            },
            media: currentBadge.icon
              ? {
                  url: currentBadge.icon,
                  kind: MediaKindEnum.Image,
                }
              : undefined,
          }}
          isPreview={false}
          showNext={false}
          showPrev={false}
          onClose={closeBadgeModal}
        />
      ) : null}
      {pointsMessage ? (
        <PointsPreviewModal open message={pointsMessage} onClose={closePointsModal} />
      ) : null}
    </>
  );
};

export default React.memo(NotificationBell);
